/*
 * parse.js — every GPS format we read, into ONE track model.
 *
 *   GpsParse.parse(u8, fileName) → Doc
 *   GpsParse.detect(u8, fileName) → 'gpx'|'kml'|'tcx'|'fit'|'geojson'|'igc'|'nmea'|'csv'|'plt'|'kmz'|null
 *
 * Doc = {
 *   format, name, creator, device, time,
 *   tracks: [Trk], waypoints: [Wpt], areas: [Area], warnings: []
 * }
 * Trk = { kind:'track'|'route', name, desc, sport, color, segs:[Seg], laps:[Lap], device:{totals} }
 * Seg = { n, lat:Float64Array, lon:Float64Array, ele:Float32Array|null, time:Float64Array|null,
 *         ch:{ hr, cad, power, temp, speed, dist, depth … : Float32Array } }   (NaN = no sample)
 * Wpt = { lat, lon, ele, time, name, desc, sym, type }
 * Area = { name, rings:[[ [lon,lat], … ]], color }
 *
 * Columns are typed arrays so a million-point track crosses the worker boundary
 * as transferables, not as a million objects.
 *
 * KMZ is unzipped by the caller (it needs a zip reader); detect() only names it.
 */
(function () {
    'use strict';
    var root = typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : this);
    var X = root.GpsXmlScan;
    var CHANNELS = ['hr', 'cad', 'power', 'temp', 'speed', 'dist', 'depth'];

    // ── segment builder ────────────────────────────────────────────────────
    function SegB() {
        this.lat = []; this.lon = []; this.ele = []; this.time = [];
        this.hasEle = false; this.hasTime = false;
        this.ch = {};
    }
    SegB.prototype.push = function (lat, lon, ele, time, ch) {
        var i = this.lat.length;
        this.lat.push(lat); this.lon.push(lon);
        this.ele.push(ele == null || !isFinite(ele) ? NaN : ele);
        this.time.push(time == null || !isFinite(time) ? NaN : time);
        if (ele != null && isFinite(ele)) this.hasEle = true;
        if (time != null && isFinite(time)) this.hasTime = true;
        if (ch) for (var k in ch) {
            var v = ch[k];
            if (v == null || !isFinite(v)) continue;
            var a = this.ch[k];
            if (!a) { a = this.ch[k] = new Array(i); for (var z = 0; z < i; z++) a[z] = NaN; }
            a[i] = v;
        }
    };
    SegB.prototype.done = function () {
        var n = this.lat.length;
        if (!n) return null;
        var s = { n: n, lat: Float64Array.from(this.lat), lon: Float64Array.from(this.lon),
                  ele: this.hasEle ? Float32Array.from(this.ele) : null,
                  time: this.hasTime ? Float64Array.from(this.time) : null, ch: {} };
        for (var k in this.ch) {
            var a = this.ch[k], t = new Float32Array(n), any = false;
            for (var i = 0; i < n; i++) { var v = a[i]; t[i] = v == null ? NaN : v; if (v == null) t[i] = NaN; else any = true; }
            if (any) s.ch[k] = t;
        }
        return s;
    };

    function newDoc(format) {
        return { format: format, name: null, creator: null, device: null, time: null,
                 tracks: [], waypoints: [], areas: [], warnings: [] };
    }

    function num(s) {
        if (s == null) return null;
        var v = parseFloat(String(s).replace(',', '.'));
        return isFinite(v) ? v : null;
    }

    // ISO 8601; a time without a zone is UTC by the GPX/TCX/KML specs (Date.parse would read it as LOCAL).
    var NOZONE = /^\d{4}-\d\d-\d\d[T ]\d\d:\d\d(:\d\d(\.\d+)?)?$/;
    function parseTime(s) {
        if (!s) return null;
        s = String(s).trim();
        if (NOZONE.test(s)) s = s.replace(' ', 'T') + 'Z';
        var t = Date.parse(s);
        return isFinite(t) ? t : null;
    }

    function validLL(lat, lon) {
        return lat != null && lon != null && isFinite(lat) && isFinite(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;
    }

    // KML colour is aabbggrr; return #rrggbb
    function kmlColor(c) {
        if (!c) return null;
        c = String(c).trim().replace(/^#/, '');
        if (/^[0-9a-fA-F]{8}$/.test(c)) return '#' + c.slice(6, 8) + c.slice(4, 6) + c.slice(2, 4);
        if (/^[0-9a-fA-F]{6}$/.test(c)) return '#' + c.slice(4, 6) + c.slice(2, 4) + c.slice(0, 2);
        return null;
    }
    function hexColor(c) {
        if (!c) return null;
        c = String(c).trim();
        if (/^#?[0-9a-fA-F]{6}$/.test(c)) return (c[0] === '#' ? c : '#' + c).toLowerCase();
        var named = { black: '#000000', darkred: '#8b0000', darkgreen: '#006400', darkyellow: '#b5b500', darkblue: '#00008b',
                      darkmagenta: '#8b008b', darkcyan: '#008b8b', lightgray: '#d3d3d3', darkgray: '#a9a9a9', red: '#ff0000',
                      green: '#00ff00', yellow: '#ffff00', blue: '#0000ff', magenta: '#ff00ff', cyan: '#00ffff', white: '#ffffff' };
        return named[c.toLowerCase()] || null;
    }

    // ── GPX 1.0 / 1.1 ──────────────────────────────────────────────────────
    function parseGpx(text) {
        var doc = newDoc('gpx');
        var stack = [];
        var cur = null;          // current trk/rte object
        var seg = null;          // SegB
        var pt = null;           // current point {lat, lon, ele, time, ch, name…}
        var buf = '';
        var h = {
            cap: false,
            open: function (ln, a) {
                stack.push(ln);
                var parent = stack.length > 1 ? stack[stack.length - 2] : null;
                switch (ln) {
                    case 'gpx':
                        doc.creator = a.creator || null;
                        if (a.version) doc.gpxVersion = a.version;
                        break;
                    case 'trk': cur = { kind: 'track', name: null, desc: null, sport: null, color: null, segs: [], laps: [], device: null }; break;
                    case 'rte': cur = { kind: 'route', name: null, desc: null, sport: null, color: null, segs: [], laps: [], device: null }; seg = new SegB(); break;
                    case 'trkseg': seg = new SegB(); break;
                    case 'trkpt': case 'rtept': case 'wpt':
                        pt = { lat: num(a.lat), lon: num(a.lon), ele: null, time: null, ch: {}, kind: ln };
                        break;
                    default:
                        buf = '';
                        h.cap = true;
                }
                if (ln === 'trkpt' || ln === 'rtept' || ln === 'wpt' || ln === 'trk' || ln === 'rte' || ln === 'trkseg' || ln === 'gpx') h.cap = false;
                void parent;
            },
            text: function (s) { buf += s; },
            close: function (ln) {
                var parent = stack.length > 1 ? stack[stack.length - 2] : null;
                var v = buf.trim();
                if (pt) {
                    switch (ln) {
                        case 'ele': pt.ele = num(v); break;
                        case 'time': pt.time = parseTime(v); break;
                        case 'name': pt.name = v; break;
                        case 'desc': case 'cmt': if (!pt.desc) pt.desc = v; break;
                        case 'sym': pt.sym = v; break;
                        case 'type': pt.type = v; break;
                        case 'hr': case 'heartrate': pt.ch.hr = num(v); break;
                        case 'cad': case 'cadence': pt.ch.cad = num(v); break;
                        case 'atemp': case 'temp': case 'wtemp': if (pt.ch.temp == null) pt.ch.temp = num(v); break;
                        case 'power': case 'PowerInWatts': case 'watts': pt.ch.power = num(v); break;
                        case 'speed': pt.ch.speed = num(v); break;
                        case 'depth': pt.ch.depth = num(v); break;
                        case 'trkpt': case 'rtept':
                            if (validLL(pt.lat, pt.lon) && seg) seg.push(pt.lat, pt.lon, pt.ele, pt.time, pt.ch);
                            else doc._badPts = (doc._badPts || 0) + 1;
                            pt = null; break;
                        case 'wpt':
                            if (validLL(pt.lat, pt.lon)) doc.waypoints.push({ lat: pt.lat, lon: pt.lon, ele: pt.ele, time: pt.time, name: pt.name || null, desc: pt.desc || null, sym: pt.sym || null, type: pt.type || null });
                            pt = null; break;
                    }
                } else if (cur) {
                    if (ln === 'name' && (parent === 'trk' || parent === 'rte')) cur.name = v;
                    else if ((ln === 'desc' || ln === 'cmt') && (parent === 'trk' || parent === 'rte') && !cur.desc) cur.desc = v;
                    else if (ln === 'type' && (parent === 'trk' || parent === 'rte')) cur.sport = v;
                    else if (ln === 'color' || ln === 'DisplayColor') cur.color = hexColor(v) || cur.color;
                    else if (ln === 'trkseg') { var s = seg && seg.done(); if (s) cur.segs.push(s); seg = null; }
                    else if (ln === 'trk') { if (cur.segs.length) doc.tracks.push(cur); cur = null; }
                    else if (ln === 'rte') { var r = seg && seg.done(); if (r) { cur.segs.push(r); doc.tracks.push(cur); } cur = null; seg = null; }
                } else {
                    if (ln === 'name' && (parent === 'metadata' || parent === 'gpx') && !doc.name) doc.name = v;
                    else if (ln === 'time' && (parent === 'metadata' || parent === 'gpx')) doc.time = parseTime(v);
                }
                stack.pop();
                buf = '';
                h.cap = false;
            }
        };
        X.scan(text, h);
        if (doc._badPts) { doc.warnings.push('bad-points:' + doc._badPts); delete doc._badPts; }
        return doc;
    }

    // ── TCX ────────────────────────────────────────────────────────────────
    function parseTcx(text) {
        var doc = newDoc('tcx');
        var stack = [];
        var trk = null, seg = null, pt = null, lap = null, cp = null;
        var buf = '';
        var h = {
            cap: false,
            open: function (ln, a) {
                stack.push(ln);
                h.cap = false;
                switch (ln) {
                    case 'Activity':
                        trk = { kind: 'track', name: null, desc: null, sport: a.Sport || null, color: null, segs: [], laps: [], device: null }; break;
                    case 'Course':
                        trk = { kind: 'route', name: null, desc: null, sport: null, color: null, segs: [], laps: [], device: null }; break;
                    case 'Lap':
                        lap = { start: parseTime(a.StartTime), timer: null, distance: null, maxSpeed: null, calories: null, avgHr: null, maxHr: null };
                        break;
                    case 'Track': seg = new SegB(); break;
                    case 'Trackpoint': pt = { lat: null, lon: null, ele: null, time: null, ch: {} }; break;
                    case 'CoursePoint': cp = { lat: null, lon: null, ele: null, time: null, name: null, type: null }; break;
                    default: buf = ''; h.cap = true;
                }
            },
            text: function (s) { buf += s; },
            close: function (ln) {
                var parent = stack.length > 1 ? stack[stack.length - 2] : null;
                var gp = stack.length > 2 ? stack[stack.length - 3] : null;
                var v = buf.trim();
                if (pt) {
                    switch (ln) {
                        case 'Time': pt.time = parseTime(v); break;
                        case 'LatitudeDegrees': pt.lat = num(v); break;
                        case 'LongitudeDegrees': pt.lon = num(v); break;
                        case 'AltitudeMeters': pt.ele = num(v); break;
                        case 'DistanceMeters': pt.ch.dist = num(v); break;
                        case 'Value': if (parent === 'HeartRateBpm') pt.ch.hr = num(v); break;
                        case 'Cadence': case 'RunCadence': pt.ch.cad = num(v); break;
                        case 'Speed': pt.ch.speed = num(v); break;
                        case 'Watts': pt.ch.power = num(v); break;
                        case 'Trackpoint':
                            if (seg) {
                                if (validLL(pt.lat, pt.lon)) seg.push(pt.lat, pt.lon, pt.ele, pt.time, pt.ch);
                                else doc._noPos = (doc._noPos || 0) + 1;
                            }
                            pt = null; break;
                    }
                } else if (cp) {
                    switch (ln) {
                        case 'Name': cp.name = v; break;
                        case 'Time': cp.time = parseTime(v); break;
                        case 'LatitudeDegrees': cp.lat = num(v); break;
                        case 'LongitudeDegrees': cp.lon = num(v); break;
                        case 'AltitudeMeters': cp.ele = num(v); break;
                        case 'PointType': cp.type = v; break;
                        case 'Notes': cp.desc = v; break;
                        case 'CoursePoint':
                            if (validLL(cp.lat, cp.lon)) doc.waypoints.push({ lat: cp.lat, lon: cp.lon, ele: cp.ele, time: cp.time, name: cp.name, desc: cp.desc || null, sym: cp.type, type: cp.type });
                            cp = null; break;
                    }
                } else if (ln === 'Track') {
                    // Structural closes first: a Track closes INSIDE a Lap, and the
                    // lap-field branch below would otherwise swallow it.
                    var s = seg && seg.done(); if (s && trk) trk.segs.push(s); seg = null;
                } else if (ln === 'Lap' && lap) {
                    if (trk) trk.laps.push(lap);
                    lap = null;
                } else if (lap && parent === 'Lap') {
                    if (ln === 'TotalTimeSeconds') lap.timer = num(v);
                    else if (ln === 'DistanceMeters') lap.distance = num(v);
                    else if (ln === 'MaximumSpeed') lap.maxSpeed = num(v);
                    else if (ln === 'Calories') lap.calories = num(v);
                } else if (lap && ln === 'Value' && gp === 'Lap') {
                    if (parent === 'AverageHeartRateBpm') lap.avgHr = num(v);
                    else if (parent === 'MaximumHeartRateBpm') lap.maxHr = num(v);
                } else if (ln === 'Id' && parent === 'Activity' && trk) {
                    trk.id = v;
                } else if (ln === 'Name' && trk && (parent === 'Course' || parent === 'Activity')) {
                    trk.name = v;
                } else if (ln === 'Name' && parent === 'Creator') {
                    doc.device = v;
                } else if (ln === 'Activity' || ln === 'Course') {
                    if (trk) {
                        if (trk.laps.length) trk.device = sumLaps(trk.laps);
                        if (trk.segs.length || trk.laps.length) doc.tracks.push(trk);
                    }
                    trk = null;
                }
                stack.pop();
                buf = '';
                h.cap = false;
            }
        };
        X.scan(text, h);
        if (doc._noPos) { doc.warnings.push('no-position:' + doc._noPos); delete doc._noPos; }
        return doc;
    }

    function sumLaps(laps) {
        var t = { timer: 0, distance: 0, calories: 0, maxSpeed: null, maxHr: null };
        var any = false;
        laps.forEach(function (l) {
            if (l.timer != null) { t.timer += l.timer; any = true; }
            if (l.distance != null) t.distance += l.distance;
            if (l.calories != null) t.calories += l.calories;
            if (l.maxSpeed != null) t.maxSpeed = Math.max(t.maxSpeed || 0, l.maxSpeed);
            if (l.maxHr != null) t.maxHr = Math.max(t.maxHr || 0, l.maxHr);
        });
        return any ? t : null;
    }

    // ── KML ────────────────────────────────────────────────────────────────
    function parseCoordList(s) {
        // "lon,lat[,alt] lon,lat[,alt] …" — separators are any whitespace; tolerate spaces after commas
        var out = [];
        var parts = String(s).replace(/,\s+/g, ',').trim().split(/\s+/);
        for (var i = 0; i < parts.length; i++) {
            if (!parts[i]) continue;
            var c = parts[i].split(',');
            var lon = parseFloat(c[0]), lat = parseFloat(c[1]);
            if (!validLL(lat, lon)) continue;
            var alt = c.length > 2 ? parseFloat(c[2]) : NaN;
            out.push([lon, lat, isFinite(alt) ? alt : null]);
        }
        return out;
    }

    function parseKml(text) {
        var doc = newDoc('kml');
        var styles = {};      // id → {color, width}
        var styleMaps = {};   // id → normal styleUrl
        var stack = [];
        var buf = '';
        var pm = null;        // current Placemark {name, desc, styleUrl, style, geoms:[], when}
        var style = null;     // Style being read
        var smId = null, smKey = null;
        var gx = null;        // current gx:Track {when:[], coord:[], arrays:{name:[]}, curArr}
        var poly = null;      // polygon being read {rings:[]}
        var docNameSet = false;
        var h = {
            cap: false,
            open: function (ln, a) {
                stack.push(ln);
                h.cap = false;
                switch (ln) {
                    case 'Placemark': pm = { name: null, desc: null, styleUrl: null, style: null, geoms: [], when: null, begin: null }; break;
                    case 'Style': style = { id: a.id || null, color: null, width: null }; break;
                    case 'StyleMap': smId = a.id || null; break;
                    case 'Track': gx = { when: [], coord: [], arrays: {}, cur: null }; break;
                    case 'SimpleArrayData': if (gx) { gx.cur = (a.name || '').toLowerCase(); gx.arrays[gx.cur] = []; } break;
                    case 'Polygon': poly = { rings: [] }; break;
                    default: buf = ''; h.cap = true;
                }
            },
            text: function (s) { buf += s; },
            close: function (ln) {
                var parent = stack.length > 1 ? stack[stack.length - 2] : null;
                var v = buf.trim();
                if (style && ln !== 'Style') {
                    if (ln === 'color' && parent === 'LineStyle') style.color = kmlColor(v);
                    else if (ln === 'color' && parent === 'PolyStyle' && !style.color) style.polyColor = kmlColor(v);
                    else if (ln === 'width' && parent === 'LineStyle') style.width = num(v);
                } else if (ln === 'Style' && style) {
                    if (pm && !style.id) pm.style = style;
                    else if (style.id) styles[style.id] = style;
                    style = null;
                }
                if (smId && ln === 'key') smKey = v;
                else if (smId && ln === 'styleUrl' && parent === 'Pair') { if (smKey === 'normal') styleMaps[smId] = v; }
                else if (ln === 'StyleMap') { smId = null; smKey = null; }

                if (gx && ln !== 'Track') {
                    if (ln === 'when') gx.when.push(parseTime(v));
                    else if (ln === 'coord') {
                        var c = v.split(/\s+/);
                        gx.coord.push([parseFloat(c[0]), parseFloat(c[1]), c.length > 2 ? parseFloat(c[2]) : NaN]);
                    } else if (ln === 'value' && gx.cur) gx.arrays[gx.cur].push(num(v));
                    else if (ln === 'SimpleArrayData') gx.cur = null;
                } else if (ln === 'Track' && gx) {
                    if (pm) pm.geoms.push({ t: 'gx', g: gx });
                    gx = null;
                }

                if (poly && ln === 'coordinates') {
                    var ring = parseCoordList(v).map(function (p) { return [p[0], p[1]]; });
                    if (ring.length >= 3) poly.rings.push(ring);
                } else if (ln === 'Polygon' && poly) {
                    if (pm && poly.rings.length) pm.geoms.push({ t: 'poly', rings: poly.rings });
                    poly = null;
                } else if (pm && ln === 'coordinates') {
                    var pts = parseCoordList(v);
                    if (parent === 'Point' && pts.length) pm.geoms.push({ t: 'pt', p: pts[0] });
                    else if ((parent === 'LineString' || parent === 'LinearRing') && pts.length) pm.geoms.push({ t: 'line', pts: pts });
                } else if (pm && ln === 'name' && parent === 'Placemark') pm.name = v;
                else if (pm && ln === 'description' && parent === 'Placemark') pm.desc = stripHtml(v);
                else if (pm && ln === 'styleUrl' && parent === 'Placemark') pm.styleUrl = v;
                else if (pm && ln === 'when' && parent === 'TimeStamp') pm.when = parseTime(v);
                else if (pm && ln === 'begin' && parent === 'TimeSpan') pm.begin = parseTime(v);
                else if (!pm && ln === 'name' && (parent === 'Document' || parent === 'Folder') && !docNameSet) { doc.name = v; docNameSet = true; }
                else if (ln === 'Placemark' && pm) { emitPlacemark(doc, pm, styles, styleMaps); pm = null; }

                stack.pop();
                buf = '';
                h.cap = false;
            }
        };
        X.scan(text, h);
        return doc;
    }

    function stripHtml(s) {
        if (!s) return s;
        return s.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim() || null;
    }

    function resolveStyle(pm, styles, styleMaps) {
        if (pm.style) return pm.style;
        if (!pm.styleUrl) return null;
        var id = pm.styleUrl.replace(/^.*#/, '');
        if (styles[id]) return styles[id];
        var n = styleMaps[id];
        if (n) return styles[n.replace(/^.*#/, '')] || null;
        return null;
    }

    function emitPlacemark(doc, pm, styles, styleMaps) {
        var st = resolveStyle(pm, styles, styleMaps);
        var color = st && (st.color || st.polyColor) || null;
        var trk = null;
        for (var i = 0; i < pm.geoms.length; i++) {
            var g = pm.geoms[i];
            if (g.t === 'pt') {
                doc.waypoints.push({ lat: g.p[1], lon: g.p[0], ele: g.p[2], time: pm.when || pm.begin || null, name: pm.name, desc: pm.desc, sym: null, type: null });
            } else if (g.t === 'line') {
                if (!trk) trk = { kind: 'track', name: pm.name, desc: pm.desc, sport: null, color: color, segs: [], laps: [], device: null };
                var sb = new SegB();
                g.pts.forEach(function (p) { sb.push(p[1], p[0], p[2], null, null); });
                var s = sb.done(); if (s) trk.segs.push(s);
            } else if (g.t === 'gx') {
                if (!trk) trk = { kind: 'track', name: pm.name, desc: pm.desc, sport: null, color: color, segs: [], laps: [], device: null };
                var G = g.g, b = new SegB();
                var map = { heartrate: 'hr', heart_rate: 'hr', hr: 'hr', cadence: 'cad', cad: 'cad', power: 'power', watts: 'power', temperature: 'temp', temp: 'temp', speed: 'speed' };
                for (var k = 0; k < G.coord.length; k++) {
                    var c = G.coord[k];
                    if (!validLL(c[1], c[0])) continue;
                    var ch = null;
                    for (var an in G.arrays) {
                        var key = map[an];
                        if (!key) continue;
                        (ch || (ch = {}))[key] = G.arrays[an][k];
                    }
                    b.push(c[1], c[0], isFinite(c[2]) ? c[2] : null, G.when[k], ch);
                }
                var gs = b.done();
                if (gs && gs.n === 1) {
                    // Google Location History writes a one-fix gx:Track for every
                    // «place visit»: that is a place, not a track.
                    doc.waypoints.push({ lat: gs.lat[0], lon: gs.lon[0], ele: gs.ele ? gs.ele[0] : null, time: gs.time ? gs.time[0] : (pm.when || pm.begin || null), name: pm.name, desc: pm.desc, sym: null, type: null });
                } else if (gs) trk.segs.push(gs);
            } else if (g.t === 'poly') {
                doc.areas.push({ name: pm.name, desc: pm.desc, rings: g.rings, color: color });
            }
        }
        if (trk && trk.segs.length) doc.tracks.push(trk);
    }

    // ── GeoJSON ────────────────────────────────────────────────────────────
    function parseGeoJson(text) {
        var doc = newDoc('geojson');
        var j = JSON.parse(text);
        var feats = j.type === 'FeatureCollection' ? j.features : j.type === 'Feature' ? [j] : [{ type: 'Feature', geometry: j, properties: {} }];
        (feats || []).forEach(function (f) {
            if (!f || !f.geometry) return;
            var p = f.properties || {};
            addGeom(doc, f.geometry, p);
        });
        return doc;
    }
    function addGeom(doc, g, p) {
        if (!g) return;
        var name = p.name || p.title || p.Name || null;
        var color = hexColor(p.stroke || p.color || p['stroke-color']) || null;
        var times = p.coordTimes || (p.coordinateProperties && p.coordinateProperties.times) || null;
        var cp = p.coordinateProperties || {};
        var hrs = p.heartRates || cp.heart || cp.heartRates || cp.hr || null;
        switch (g.type) {
            case 'Point':
                if (validLL(g.coordinates[1], g.coordinates[0])) doc.waypoints.push({ lat: g.coordinates[1], lon: g.coordinates[0], ele: g.coordinates[2] != null ? g.coordinates[2] : null, time: parseTime(p.time), name: name, desc: p.desc || p.description || null, sym: p.sym || null, type: p.type || null });
                break;
            case 'MultiPoint':
                g.coordinates.forEach(function (c) { addGeom(doc, { type: 'Point', coordinates: c }, p); });
                break;
            case 'LineString': case 'MultiLineString': {
                var lines = g.type === 'LineString' ? [g.coordinates] : g.coordinates;
                var trk = { kind: 'track', name: name, desc: p.desc || p.description || null, sport: p.type || p.sport || null, color: color, segs: [], laps: [], device: null };
                lines.forEach(function (line, li) {
                    var b = new SegB();
                    var tl = times ? (g.type === 'LineString' ? times : times[li]) : null;
                    var hl = hrs ? (g.type === 'LineString' ? hrs : hrs[li]) : null;
                    line.forEach(function (c, i) {
                        if (!validLL(c[1], c[0])) return;
                        b.push(c[1], c[0], c[2] != null ? c[2] : null, tl ? parseTime(tl[i]) : null, hl ? { hr: hl[i] } : null);
                    });
                    var s = b.done(); if (s) trk.segs.push(s);
                });
                if (trk.segs.length) doc.tracks.push(trk);
                break;
            }
            case 'Polygon': case 'MultiPolygon': {
                var polys = g.type === 'Polygon' ? [g.coordinates] : g.coordinates;
                polys.forEach(function (rings) {
                    doc.areas.push({ name: name, desc: p.desc || null, rings: rings.map(function (r) { return r.map(function (c) { return [c[0], c[1]]; }); }), color: color });
                });
                break;
            }
            case 'GeometryCollection':
                (g.geometries || []).forEach(function (x) { addGeom(doc, x, p); });
                break;
        }
    }

    // ── IGC (gliders, paragliders) ─────────────────────────────────────────
    function parseIgc(text) {
        var doc = newDoc('igc');
        var lines = text.split(/\r?\n/);
        var day = null, prevSec = -1, dayOff = 0;
        var b = new SegB();
        var pilot = null, glider = null;
        for (var i = 0; i < lines.length; i++) {
            var L = lines[i];
            if (!L) continue;
            var c0 = L.charCodeAt(0);
            if (c0 === 72) { // 'H'
                var m = /^H[FOP]DTE(?:DATE:)?(\d\d)(\d\d)(\d\d)/.exec(L);
                if (m) day = Date.UTC(2000 + +m[3] - (+m[3] > 80 ? 100 : 0), +m[2] - 1, +m[1]);
                var pm = /^H[FOP]PLT[^:]*:(.*)$/.exec(L); if (pm) pilot = pm[1].trim() || null;
                var gm = /^H[FOP]GTY[^:]*:(.*)$/.exec(L); if (gm) glider = gm[1].trim() || null;
            } else if (c0 === 65 && !doc.device) { // 'A'
                doc.device = L.slice(1).trim() || null;
            } else if (c0 === 66 && L.length >= 35) { // 'B'
                var hh = +L.substr(1, 2), mi = +L.substr(3, 2), ss = +L.substr(5, 2);
                var lat = +L.substr(7, 2) + (+L.substr(9, 5)) / 60000;
                if (L[14] === 'S') lat = -lat;
                var lon = +L.substr(15, 3) + (+L.substr(18, 5)) / 60000;
                if (L[23] === 'W') lon = -lon;
                var pAlt = parseInt(L.substr(25, 5), 10), gAlt = parseInt(L.substr(30, 5), 10);
                var sec = hh * 3600 + mi * 60 + ss;
                if (prevSec >= 0 && sec < prevSec - 3600) dayOff += 86400; // crossed UTC midnight
                prevSec = sec;
                var t = day != null ? day + (sec + dayOff) * 1000 : null;
                var alt = isFinite(gAlt) && gAlt !== 0 ? gAlt : (isFinite(pAlt) ? pAlt : null);
                if (validLL(lat, lon)) b.push(lat, lon, alt, t, isFinite(pAlt) && pAlt !== 0 ? { palt: pAlt } : null);
            } else if (c0 === 67 && L.length >= 18) { // 'C' task declaration waypoint
                var cl = /^C(\d\d)(\d{5})([NS])(\d{3})(\d{5})([EW])(.*)$/.exec(L);
                if (cl) {
                    var wl = +cl[1] + (+cl[2]) / 60000, wo = +cl[4] + (+cl[5]) / 60000;
                    if (cl[3] === 'S') wl = -wl; if (cl[6] === 'W') wo = -wo;
                    if (validLL(wl, wo) && (wl !== 0 || wo !== 0)) doc.waypoints.push({ lat: wl, lon: wo, ele: null, time: null, name: cl[7].trim() || null, desc: null, sym: 'task', type: 'task' });
                }
            }
        }
        var s = b.done();
        if (s) doc.tracks.push({ kind: 'track', name: [pilot, glider].filter(Boolean).join(' · ') || null, desc: null, sport: 'flight', color: null, segs: [s], laps: [], device: null });
        return doc;
    }

    // ── NMEA 0183 ──────────────────────────────────────────────────────────
    function nmeaLL(v, hemi, degLen) {
        if (!v) return null;
        var d = parseFloat(v.slice(0, degLen)), m = parseFloat(v.slice(degLen));
        if (!isFinite(d) || !isFinite(m)) return null;
        var r = d + m / 60;
        return hemi === 'S' || hemi === 'W' ? -r : r;
    }
    function parseNmea(text) {
        var doc = newDoc('nmea');
        var b = new SegB();
        var lines = text.split(/\r?\n/);
        var date = null;           // ms at 00:00 UTC of the current day (from RMC)
        var pending = null;        // point built from sentences sharing one timestamp
        var lastTimeStr = null;
        function flush() {
            if (pending && validLL(pending.lat, pending.lon)) {
                var t = null;
                if (date != null && pending.tod != null) t = date + pending.tod;
                b.push(pending.lat, pending.lon, pending.ele, t, pending.speed != null ? { speed: pending.speed } : null);
            }
            pending = null;
        }
        for (var i = 0; i < lines.length; i++) {
            var L = lines[i].trim();
            var dollar = L.indexOf('$');
            if (dollar < 0) continue;
            L = L.slice(dollar);
            var star = L.indexOf('*');
            if (star > 0) L = L.slice(0, star);
            var f = L.split(',');
            var type = f[0].slice(3);
            if (type !== 'RMC' && type !== 'GGA') continue;
            var ts = f[1];
            if (ts !== lastTimeStr) { flush(); lastTimeStr = ts; }
            if (!pending) pending = { lat: null, lon: null, ele: null, speed: null, tod: null };
            if (ts && ts.length >= 6) pending.tod = ((+ts.slice(0, 2)) * 3600 + (+ts.slice(2, 4)) * 60 + parseFloat(ts.slice(4))) * 1000;
            if (type === 'RMC') {
                if (f[2] !== 'A') { pending.void = true; continue; }
                pending.lat = nmeaLL(f[3], f[4], 2);
                pending.lon = nmeaLL(f[5], f[6], 3);
                var kn = parseFloat(f[7]);
                if (isFinite(kn)) pending.speed = kn * 0.514444;
                if (f[9] && f[9].length === 6) {
                    var yy = +f[9].slice(4, 6);
                    date = Date.UTC(yy < 80 ? 2000 + yy : 1900 + yy, +f[9].slice(2, 4) - 1, +f[9].slice(0, 2));
                }
            } else {
                if (f[6] === '0') continue;
                if (pending.lat == null) { pending.lat = nmeaLL(f[2], f[3], 2); pending.lon = nmeaLL(f[4], f[5], 3); }
                var alt = parseFloat(f[9]);
                if (isFinite(alt)) pending.ele = alt;
            }
        }
        flush();
        var s = b.done();
        if (s) doc.tracks.push({ kind: 'track', name: null, desc: null, sport: null, color: null, segs: [s], laps: [], device: null });
        return doc;
    }

    // ── OziExplorer PLT ────────────────────────────────────────────────────
    function parsePlt(text) {
        var doc = newDoc('plt');
        var lines = text.split(/\r?\n/);
        var trk = { kind: 'track', name: null, desc: null, sport: null, color: null, segs: [], laps: [], device: null };
        if (lines[4]) {
            var hdr = lines[4].split(',');
            if (hdr[3]) trk.name = hdr[3].trim() || null;
            var col = parseInt(hdr[2], 10);
            if (isFinite(col)) trk.color = '#' + ('000000' + (col & 0xFFFFFF).toString(16)).slice(-6).replace(/(..)(..)(..)/, '$3$2$1');
        }
        var b = new SegB();
        for (var i = 6; i < lines.length; i++) {
            var f = lines[i].split(',');
            if (f.length < 5) continue;
            var lat = parseFloat(f[0]), lon = parseFloat(f[1]);
            if (!validLL(lat, lon)) continue;
            if (f[2] && f[2].trim() === '1' && b.lat.length) { var s0 = b.done(); if (s0) trk.segs.push(s0); b = new SegB(); }
            var ft = parseFloat(f[3]);
            var ele = isFinite(ft) && ft !== -777 ? ft * 0.3048 : null;
            var days = parseFloat(f[4]);
            var t = isFinite(days) && days > 1 ? Math.round((days - 25569) * 86400000) : null; // Delphi epoch 1899-12-30
            b.push(lat, lon, ele, t, null);
        }
        var s = b.done(); if (s) trk.segs.push(s);
        if (trk.segs.length) doc.tracks.push(trk);
        return doc;
    }

    // ── CSV / TSV with a header ────────────────────────────────────────────
    var COLS = {
        lat: /^(lat|latitude|y|широта|breite|breitengrad|latitud|latitude_deg|lat_deg|위도|lintang|خط العرض|широта_град)$/i,
        lon: /^(lon|lng|long|longitude|x|долгота|länge|laengengrad|längengrad|longitud|longitude_deg|lon_deg|경도|bujur|خط الطول|довгота)$/i,
        ele: /^(ele|elevation|alt|altitude|height|высота|höhe|hoehe|altitud|elevación|altitude_m|ele_m|고도|ketinggian|الارتفاع|висота)$/i,
        time: /^(time|time_utc|timestamp|timestamp_utc|datetime|date_time|utc|время|zeit|tiempo|hora|temps|horário|시간|waktu|الوقت|час|date)$/i,
        hr: /^(hr|heart_?rate|heartrate|пульс|puls)$/i,
        cad: /^(cad|cadence|каденс)$/i,
        power: /^(power|watts|мощность)$/i,
        speed: /^(speed|скорость|geschwindigkeit|velocidad)$/i,
        name: /^(name|title|название|имя)$/i
    };
    function parseCsv(text) {
        var doc = newDoc('csv');
        var lines = text.split(/\r?\n/).filter(function (l) { return l.trim(); });
        if (!lines.length) return doc;
        var first = lines[0];
        var delim = [';', '\t', ','].reduce(function (best, d) { return first.split(d).length > first.split(best).length ? d : best; }, ',');
        var head = splitCsv(first, delim).map(function (s) { return s.trim().replace(/^"|"$/g, ''); });
        var idx = {};
        head.forEach(function (hname, i) {
            var clean = hname.replace(/\s*\(.*\)\s*$/, '').replace(/[\s_-]+(deg|m|meters|metres)$/i, '').trim();
            for (var k in COLS) if (idx[k] == null && COLS[k].test(clean)) idx[k] = i;
        });
        var start = 1;
        if (idx.lat == null || idx.lon == null) {
            // no usable header: assume «lat,lon[,ele]» rows
            var probe = splitCsv(first, delim);
            if (probe.length >= 2 && isFinite(parseFloat(probe[0])) && isFinite(parseFloat(probe[1]))) { idx = { lat: 0, lon: 1, ele: probe.length > 2 ? 2 : null }; start = 0; }
            else throw new Error('csv-no-coords');
        }
        var decComma = delim !== ',';
        var b = new SegB();
        for (var i = start; i < lines.length; i++) {
            var f = splitCsv(lines[i], delim);
            var g = function (k) { if (idx[k] == null) return null; var s = f[idx[k]]; if (s == null) return null; s = s.trim().replace(/^"|"$/g, ''); if (decComma) s = s.replace(',', '.'); return s; };
            var lat = parseFloat(g('lat')), lon = parseFloat(g('lon'));
            if (!validLL(lat, lon)) continue;
            var tRaw = g('time'), t = null;
            if (tRaw) {
                if (/^\d{9,13}(\.\d+)?$/.test(tRaw)) { var tn = parseFloat(tRaw); t = tn < 1e11 ? tn * 1000 : tn; }
                else t = parseTime(tRaw);
            }
            b.push(lat, lon, num(g('ele')), t, { hr: num(g('hr')), cad: num(g('cad')), power: num(g('power')), speed: num(g('speed')) });
        }
        var s = b.done();
        if (s) doc.tracks.push({ kind: 'track', name: null, desc: null, sport: null, color: null, segs: [s], laps: [], device: null });
        return doc;
    }
    function splitCsv(line, d) {
        if (line.indexOf('"') < 0) return line.split(d);
        var out = [], cur = '', q = false;
        for (var i = 0; i < line.length; i++) {
            var c = line[i];
            if (c === '"') { if (q && line[i + 1] === '"') { cur += '"'; i++; } else q = !q; }
            else if (c === d && !q) { out.push(cur); cur = ''; }
            else cur += c;
        }
        out.push(cur);
        return out;
    }

    // ── FIT ────────────────────────────────────────────────────────────────
    var SPORTS = { 0: 'generic', 1: 'running', 2: 'cycling', 3: 'transition', 4: 'fitness_equipment', 5: 'swimming', 6: 'basketball', 7: 'soccer', 8: 'tennis', 10: 'training', 11: 'walking', 12: 'cross_country_skiing', 13: 'alpine_skiing', 14: 'snowboarding', 15: 'rowing', 16: 'mountaineering', 17: 'hiking', 18: 'multisport', 19: 'paddling', 20: 'flying', 21: 'e_biking', 22: 'motorcycling', 23: 'boating', 24: 'driving', 25: 'golf', 26: 'hang_gliding', 27: 'horseback_riding', 28: 'hunting', 29: 'fishing', 30: 'inline_skating', 31: 'rock_climbing', 32: 'sailing', 33: 'ice_skating', 34: 'sky_diving', 35: 'snowshoeing', 36: 'snowmobiling', 37: 'stand_up_paddleboarding', 38: 'surfing', 39: 'wakeboarding', 40: 'water_skiing', 41: 'kayaking', 42: 'rafting', 43: 'windsurfing', 44: 'kitesurfing', 45: 'tactical', 46: 'jumpmaster', 47: 'boxing', 48: 'floor_climbing', 53: 'diving', 62: 'hiit', 64: 'racket' };
    var MANUF = { 1: 'Garmin', 15: 'Dynastream', 23: 'Suunto', 32: 'Wahoo', 69: 'Stryd', 76: 'Moxy', 89: 'Tacx', 107: 'Magene', 123: 'Polar', 142: 'COROS', 255: 'Development', 260: 'Zwift', 265: 'Strava', 282: 'Huawei', 286: 'Hammerhead', 289: 'Apple', 294: 'Coros', 315: 'Bryton', 324: 'Amazfit' };

    function parseFit(u8) {
        var D = root.GpsFitDecode;
        var r = D.decode(u8);
        var doc = newDoc('fit');
        if (!r.crcOk) doc.warnings.push('fit-crc');
        if (r.truncated) doc.warnings.push('fit-truncated');
        r.warnings.forEach(function (w) { doc.warnings.push(w); });
        var SEMI = D.SEMI_TO_DEG, EP = D.EPOCH_MS;
        var sessions = [], laps = [], courseName = null, sportName = null, isCourse = false;
        var b = new SegB();
        var tsMs = function (v) { return v == null ? null : EP + v * 1000; };
        var devPower = null, devHr = null;
        for (var key in r.dev) {
            var dd = r.dev[key], nm = (dd.name || '').toLowerCase();
            if ((dd.nativeMesg === 20 && dd.nativeField === 7) || nm === 'power' || nm === 'watts') devPower = devPower || key;
            if ((dd.nativeMesg === 20 && dd.nativeField === 3) || nm === 'heart_rate' || nm === 'heartrate') devHr = devHr || key;
        }
        var gapPending = false;
        for (var i = 0; i < r.messages.length; i++) {
            var m = r.messages[i], f = m.f;
            switch (m.g) {
                case 0: // file_id
                    if (f[0] === 6) isCourse = true;
                    if (f[1] != null) doc.device = (MANUF[f[1]] || ('#' + f[1])) + (f[8] ? ' ' + f[8] : '');
                    if (f[4] != null) doc.time = tsMs(f[4]);
                    break;
                case 23: // device_info
                    if (f[27] && (!doc.device || /^#|^Garmin$/.test(doc.device))) doc.device = (MANUF[f[2]] ? MANUF[f[2]] + ' ' : '') + f[27];
                    break;
                case 12: if (f[3]) sportName = f[3]; break;
                case 31: if (f[5]) courseName = f[5]; break;
                case 21: // event: timer stop → next point starts a new segment only if position jumps; we keep one segment but record pauses
                    if (f[0] === 0 && (f[1] === 1 || f[1] === 4)) gapPending = true;
                    break;
                case 20: { // record
                    var lat = f[0] != null ? f[0] * SEMI : null, lon = f[1] != null ? f[1] * SEMI : null;
                    var alt = f[78] != null ? f[78] / 5 - 500 : (f[2] != null ? f[2] / 5 - 500 : null);
                    var spd = f[73] != null ? f[73] / 1000 : (f[6] != null ? f[6] / 1000 : null);
                    var ch = {
                        hr: f[3] != null ? f[3] : (devHr && m.d ? m.d[devHr] : null),
                        cad: f[4] != null ? f[4] + (f[53] != null ? f[53] / 128 : 0) : null,
                        dist: f[5] != null ? f[5] / 100 : null,
                        speed: spd,
                        power: f[7] != null ? f[7] : (devPower && m.d ? m.d[devPower] : null),
                        temp: f[13] != null ? f[13] : null,
                        depth: f[92] != null ? f[92] / 1000 : null
                    };
                    if (gapPending) { ch.pause = 1; gapPending = false; }
                    if (validLL(lat, lon)) b.push(lat, lon, alt, tsMs(f[253]), ch);
                    else doc._noPos = (doc._noPos || 0) + 1;
                    break;
                }
                case 19: // lap
                    laps.push({ start: tsMs(f[2]), end: tsMs(f[253]), elapsed: f[7] != null ? f[7] / 1000 : null, timer: f[8] != null ? f[8] / 1000 : null,
                                distance: f[9] != null ? f[9] / 100 : null, ascent: f[21] != null ? f[21] : null, descent: f[22] != null ? f[22] : null,
                                avgHr: f[15], maxHr: f[16], calories: f[11], maxSpeed: f[111] != null ? f[111] / 1000 : (f[14] != null ? f[14] / 1000 : null) });
                    break;
                case 18: // session
                    sessions.push({ start: tsMs(f[2]), sport: SPORTS[f[5]] || null, elapsed: f[7] != null ? f[7] / 1000 : null, timer: f[8] != null ? f[8] / 1000 : null,
                                    distance: f[9] != null ? f[9] / 100 : null, calories: f[11], ascent: f[22] != null ? f[22] : null, descent: f[23] != null ? f[23] : null,
                                    avgHr: f[16], maxHr: f[17], avgCad: f[18], avgPower: f[20], maxPower: f[21], np: f[34],
                                    avgSpeed: f[124] != null ? f[124] / 1000 : (f[14] != null ? f[14] / 1000 : null),
                                    maxSpeed: f[125] != null ? f[125] / 1000 : (f[15] != null ? f[15] / 1000 : null) });
                    break;
                case 32: // course_point
                    if (f[2] != null && f[3] != null) doc.waypoints.push({ lat: f[2] * SEMI, lon: f[3] * SEMI, ele: null, time: tsMs(f[1]), name: f[6] || null, desc: null, sym: null, type: f[5] != null ? String(f[5]) : null });
                    break;
            }
        }
        var s = b.done();
        if (s && s.ch.pause) { s.pause = s.ch.pause; delete s.ch.pause; }
        var sess = sessions[0] || null;
        if (sessions.length > 1) {
            // multisport: sum the legs for the headline totals
            sess = sessions.reduce(function (a, x) {
                ['elapsed', 'timer', 'distance', 'calories', 'ascent', 'descent'].forEach(function (k) { if (x[k] != null) a[k] = (a[k] || 0) + x[k]; });
                a.maxHr = Math.max(a.maxHr || 0, x.maxHr || 0) || null;
                return a;
            }, { sport: 'multisport', start: sessions[0].start });
        }
        if (s || laps.length || sess) {
            doc.tracks.push({ kind: isCourse ? 'route' : 'track', name: courseName || null, desc: null,
                              sport: (sess && sess.sport) || sportName || null, color: null, segs: s ? [s] : [], laps: laps, device: sess });
        }
        if (doc._noPos) { doc.warnings.push('no-position:' + doc._noPos); delete doc._noPos; }
        return doc;
    }

    // ── detection & entry point ────────────────────────────────────────────
    function headText(u8, n) {
        var s = '', lim = Math.min(u8.length, n);
        for (var i = 0; i < lim; i++) { var c = u8[i]; if (c) s += String.fromCharCode(c); }
        return s;
    }
    function detect(u8, fileName) {
        var ext = (String(fileName || '').match(/\.([a-z0-9]+)$/i) || [])[1];
        ext = ext ? ext.toLowerCase() : '';
        if (u8.length >= 12 && root.GpsFitDecode && root.GpsFitDecode.isFit(u8)) return 'fit';
        if (u8.length >= 4 && u8[0] === 0x50 && u8[1] === 0x4B && u8[2] === 3 && u8[3] === 4) return 'kmz';
        var h = headText(u8, 4096);
        if (/<gpx[\s>]/i.test(h)) return 'gpx';
        if (/<kml[\s>]/i.test(h)) return 'kml';
        if (/<TrainingCenterDatabase[\s>]/i.test(h)) return 'tcx';
        var t = h.replace(/^\uFEFF/, '').trim();
        if (/^[\[{]/.test(t) && /"type"\s*:\s*"(FeatureCollection|Feature|LineString|MultiLineString|Point|GeometryCollection|Polygon)"/.test(h)) return 'geojson';
        if (/^A[A-Z0-9]{3}/.test(t) && /\nB\d{6}\d{7}[NS]\d{8}[EW]/.test(h)) return 'igc';
        if (/^\s*OziExplorer Track Point File/i.test(t)) return 'plt';
        if (/\$(GP|GN|GL|GA|BD)(RMC|GGA),/.test(h)) return 'nmea';
        if (ext === 'igc') return 'igc';
        if (ext === 'gpx') return 'gpx';
        if (ext === 'kml') return 'kml';
        if (ext === 'tcx') return 'tcx';
        if (ext === 'geojson' || ext === 'json') return 'geojson';
        if (ext === 'plt') return 'plt';
        if (ext === 'nmea' || ext === 'nma' || ext === 'log') return 'nmea';
        if (ext === 'csv' || ext === 'txt' || ext === 'tsv') return 'csv';
        return null;
    }

    function parse(u8, fileName, fmt) {
        fmt = fmt || detect(u8, fileName);
        if (!fmt) throw new Error('unknown-format');
        if (fmt === 'fit') return parseFit(u8);
        if (fmt === 'kmz') throw new Error('kmz-needs-unzip');
        var text = X.decodeBytes(u8);
        switch (fmt) {
            case 'gpx': return parseGpx(text);
            case 'kml': return parseKml(text);
            case 'tcx': return parseTcx(text);
            case 'geojson': return parseGeoJson(text);
            case 'igc': return parseIgc(text);
            case 'nmea': return parseNmea(text);
            case 'plt': return parsePlt(text);
            case 'csv': return parseCsv(text);
        }
        throw new Error('unknown-format');
    }

    function isZip(h) { return h.length >= 4 && h[0] === 0x50 && h[1] === 0x4B && (h[2] === 3 || h[2] === 5) && (h[3] === 4 || h[3] === 6); }
    function isGzip(h) { return h.length >= 2 && h[0] === 0x1F && h[1] === 0x8B; }

    /* bytes + a name → Doc, unwrapping gzip (Strava's .fit.gz) and KMZ. Shared
     * by the worker and by the page's own fallback, so both read a file the
     * same way. Needs fflate for the unwrapping. */
    function bytesToDoc(u8, name, depth) {
        depth = depth || 0;
        var F = root.fflate;
        if (isGzip(u8)) {
            if (!F || depth > 2) throw new Error('fflate-missing');
            return bytesToDoc(F.gunzipSync(u8), String(name || '').replace(/\.gz$/i, ''), depth + 1);
        }
        var fmt = detect(u8, name);
        if (fmt === 'kmz' || (isZip(u8) && /\.kmz$/i.test(name || ''))) {
            if (!F) throw new Error('fflate-missing');
            var files = F.unzipSync(u8, { filter: function (f) { return /\.kml$/i.test(f.name); } });
            var names = Object.keys(files);
            if (!names.length) throw new Error('kmz-no-kml');
            // doc.kml by convention; otherwise the shallowest .kml
            names.sort(function (a, b) { return (a.toLowerCase() === 'doc.kml' ? -1 : 0) - (b.toLowerCase() === 'doc.kml' ? -1 : 0) || a.split('/').length - b.split('/').length; });
            var doc = parse(files[names[0]], names[0], 'kml');
            doc.format = 'kmz';
            return doc;
        }
        return parse(u8, name, fmt);
    }

    root.GpsParse = { parse: parse, detect: detect, bytesToDoc: bytesToDoc, isZip: isZip, isGzip: isGzip, parseTime: parseTime, kmlColor: kmlColor, CHANNELS: CHANNELS, _SegB: SegB };
})();
